// Frozen ASIS USB control protocol (v1). All encoders/decoders
// operate on UTF-8 strings; transport layer decides byte framing.
//
// Wire format (per ../device-headless/firmware/USB-PROTOCOL.md):
//   request:  {"id":"...","cmd":"...","<fields>":<value>}\n
//   reply:    ASIS {"id":"...","ok":true,"result":{...}}\n
//             ASIS {"id":"...","ok":false,"error":{"code":"...","message":"..."}}\n
//   event:    ASIS {"event":"state|pairing",...}\n
//
// Rules respected here:
// - id matches [A-Za-z0-9_.:-]{1,32}.
// - cmd is one of the documented commands.
// - JSON payload (excluding final \n) is bounded to MAX_LINE_BYTES (768 by default).
// - Embedded \0 / \n inside any string field are rejected.
// - Events are not responses: they never carry `id` and never satisfy a pending command.
// - Bad/unknown ids arriving on the wire are ignored.

export const MAX_LINE_BYTES = 768;
export const MAX_RESPONSE_LINE_BYTES = 49_152;
export const MAX_CAMERA_JPEG_BYTES = 32_768;
export const DEFAULT_ID_LEN_MIN = 1;
export const DEFAULT_ID_LEN_MAX = 32;

export const ID_REGEX = /^[A-Za-z0-9_.:-]{1,32}$/;

export const COMMANDS = Object.freeze([
  'status',
  'wifi.set',
  'pair',
  'sd.check',
  'audio.enable',
  'session.start',
  'session.stop',
  'camera.capture',
] as const);
export type Command = typeof COMMANDS[number];

export interface StatusResult {
  protocol: number;
  firmware: string;
  deviceId: string;
  flashBytes: number;
  psramBytes: number;
  wifiConnected: boolean;
  ip: string;
  phase: 'boot' | 'idle' | 'pairing' | 'ready' | 'error' | 'blocked' | string;
  audioEnabled: boolean;
  sdChecked: boolean;
  sdAvailable: boolean;
  sdCapacityBytes: number;
  cameraSupported?: boolean;
  cameraBusy?: boolean;
  cameraLiveSupported?: boolean;
  cameraMaxJpegBytes?: number;
  sessionActive?: boolean;
  sessionReady?: boolean;
  provider?: string;
  audioReady?: boolean;
  capturing?: boolean;
  heapBytes?: number;
}

interface CameraMetadata { mimeType: 'image/jpeg'; width: 320; height: 240; bytes: number }
export interface CameraPreviewResult extends CameraMetadata { target: 'preview'; data: string }
export interface CameraLiveResult extends CameraMetadata { target: 'live'; submitted: true; promptSubmitted: boolean }

export type PairResult = { paired: true } | { code: string; expiresInMs: number };

export interface WifiSetResult {
  stored: boolean;
  connected: boolean;
}

export interface SdCheckResult {
  available: boolean;
  type: 'SDHC' | 'SDSC' | 'MMC' | '';
  capacityBytes: number;
  totalBytes: number;
  usedBytes: number;
}

export interface SuccessEnvelope<T> { id: string; ok: true; result: T }
export interface ErrorEnvelope { id: string; ok: false; error: { code: string; message: string } }
export type ResponseEnvelope<T = unknown> = SuccessEnvelope<T> | ErrorEnvelope;

export type WireEvent =
  | { event: 'state'; phase?: string; audio?: boolean; wifi?: boolean; ip?: string; [k: string]: unknown }
  | { event: 'pairing'; code: string; expiresInMs: number }
  | { event: 'camera_error'; code: 'unsupported_image' | 'image_rate_limited' };

export const ERROR_CODES = Object.freeze([
  'bad_request',
  'not_paired',
  'audio_disabled',
  'busy',
  'io',
  'unsupported',
  'camera_busy', 'camera_init_failed', 'camera_capture_failed', 'camera_too_large', 'camera_memory',
  'session_not_ready', 'unsupported_provider', 'camera_rate_limited', 'transport_error',
] as const);
export type ErrorCode = typeof ERROR_CODES[number];

// Human-safe error copy. Never reflects raw payload fields.
export const ERROR_COPY: Record<ErrorCode, string> = Object.freeze({
  bad_request: 'Petición rechazada por el dispositivo.',
  not_paired: 'El dispositivo aún no está vinculado a tu cuenta.',
  audio_disabled: 'Activa el audio antes de iniciar la sesión.',
  busy: 'Hay una sesión de voz en curso.',
  io: 'No se pudo acceder al almacenamiento del dispositivo.',
  unsupported: 'La operación no está disponible en este firmware.',
  camera_busy: 'La cámara o la conversación están ocupadas. Vuelve a intentarlo cuando terminen.',
  camera_init_failed: 'No se pudo iniciar la cámara. Comprueba su conexión.',
  camera_capture_failed: 'No se pudo tomar la foto.',
  camera_too_large: 'La foto supera el tamaño permitido por el dispositivo.',
  camera_memory: 'El dispositivo no tiene memoria disponible para esta foto.',
  session_not_ready: 'Inicia una sesión en el dispositivo y espera a que esté lista antes de compartir.',
  unsupported_provider: 'Esta sesión no admite fotos. Se necesita Gemini Live.',
  camera_rate_limited: 'Espera un momento antes de compartir otra foto.',
  transport_error: 'No se pudo enviar la foto desde el dispositivo.',
});

export function safeCopyForCode(code: string): string {
  if (typeof code !== 'string') return 'Error de comunicación con el dispositivo.';
  if ((ERROR_CODES as readonly string[]).includes(code)) return ERROR_COPY[code as ErrorCode];
  return 'Error de comunicación con el dispositivo.';
}
